import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { DEFAULT_ITEMS_PER_PAGE } from "../../constants/app";
import { TSelectCandidateForVacancyPayload } from "../../models/vacancy";
import { setTokenAction } from "../auth/slice";
import {
  getAllPositionsListAction,
  setHistoryVacanciesAction,
  setScheduledVacanciesAction,
  setUrgentVacanciesAction,
} from "./slice";
import {
  TActiveVacancyListResponse,
  TActiveVacancyParams,
  TCancelVacancyResponse,
  TCreateVacancyResponse,
  TGetActiveCandidatesParams,
  TGetAllActiveCandidatesResponse,
  TGetAllPositionsResponse,
  TGetAppliedCandidatesDetailsForVacancyResponse,
  TGetCentreDetailsForVacancyResponse,
  TGetHistoryVacancyListResponse,
  TGetSchduledVacancyListResponse,
  TGetUrgentVacancyListResponse,
  TGetVacancyResponse,
  THistoryVacancyParams,
  TNonRatedCandidatesResponse,
  TPosition,
  TRateCandidateParams,
  TRateCandidateResponse,
  TSelectCandidateForVacancyResponse,
  TToggleCandidatePreferredParams,
  TToggleCandidatePreferredResponse,
  TVacancyHistoryListResponse,
  VacancyFormAPIPayload,
} from "./types";

/**
 * GET method
 * getAllPositions - To get all candidate positions list
 */
export const getAllPositions = createAsyncThunk(
  "vacancy/getAllPositions",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetAllPositionsResponse> = await axios.get(
        `/v1/positions`
      );
      const { token, ...rest } = response.data.data;
      const positions = Object.values(rest).filter(
        (item): item is TPosition => typeof item === "object" && "uuid" in item
      );

      await dispatch(setTokenAction(token));
      await dispatch(getAllPositionsListAction(positions));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * GET method
 * getAllActiveCandidates - To get all candidate positions list
 */
export const getAllActiveCandidates = createAsyncThunk(
  "vacancy/getAllActiveCandidates",
  async (
    payload: TGetActiveCandidatesParams,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response: AxiosResponse<TGetAllActiveCandidatesResponse> =
        await axios.post(`/v1/available-candidates`, payload);
      const { token } = response.data.data;
      await dispatch(setTokenAction(token));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * createVacancyThunk - Submit the form to create the vacancy
 */
export const createVacancyThunk = createAsyncThunk(
  "vacancy/createVacancyThunk",
  async (data: VacancyFormAPIPayload, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TCreateVacancyResponse> = await axios.post(
        `/v1/vacancies`,
        data
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

// GET vacancy by UUID
export const getVacancyById = createAsyncThunk(
  "vacancy/getVacancyById",
  async (uuid: string, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetVacancyResponse> = await axios.get(
        `/v1/vacancy-details/${uuid}`
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

// PUT update vacancy
export const updateVacancyThunk = createAsyncThunk(
  "vacancy/updateVacancyThunk",
  async (
    { uuid, data }: { uuid: string; data: VacancyFormAPIPayload },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response: AxiosResponse<TCreateVacancyResponse> = await axios.put(
        `/v1/vacancies/${uuid}`,
        data
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

// GET active vacancies
export const getActiveVacancies = createAsyncThunk(
  "vacancy/getActiveVacancies",
  async (params: TActiveVacancyParams, { rejectWithValue, dispatch }) => {
    try {
      const {
        pending_sort_by,
        pending_sort_order,
        pending_vacancy_page,
        pending_per_page,
        accepted_sort_by,
        accepted_sort_order,
        accepted_vacancy_page,
        accepted_per_page,
        centre_uuid,
      } = params;

      const queryParams = new URLSearchParams();

      if (pending_sort_by)
        queryParams.append("pending_sort_by", pending_sort_by);
      if (pending_sort_order)
        queryParams.append("pending_sort_order", pending_sort_order);
      if (typeof pending_vacancy_page === "number")
        queryParams.append(
          "pending_vacancy_page",
          String(pending_vacancy_page)
        );
      if (typeof pending_per_page === "number")
        queryParams.append("pending_per_page", String(pending_per_page));

      if (accepted_sort_by)
        queryParams.append("accepted_sort_by", accepted_sort_by);
      if (accepted_sort_order)
        queryParams.append("accepted_sort_order", accepted_sort_order);
      if (typeof accepted_vacancy_page === "number")
        queryParams.append(
          "accepted_vacancy_page",
          String(accepted_vacancy_page)
        );
      if (typeof accepted_per_page === "number")
        queryParams.append("accepted_per_page", String(accepted_per_page));

      // Only include centre_uuid if it's valid
      let rawCentreUUIDParam = "";
      if (Array.isArray(centre_uuid)) {
        rawCentreUUIDParam = `&centre_uuid=[${centre_uuid
          .map((uuid) => `'${uuid}'`)
          .join(",")}]`;
      } else if (typeof centre_uuid === "string" && centre_uuid.trim() !== "") {
        rawCentreUUIDParam = `&centre_uuid=['${centre_uuid}']`;
      }

      const finalUrl = `/v1/vacancies/active?${queryParams.toString()}${rawCentreUUIDParam}`;

      const response: AxiosResponse<TActiveVacancyListResponse> =
        await axios.get(finalUrl);

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const toggleCandidatePreferredStatus = createAsyncThunk(
  "candidate/markPreferred",
  async (
    payload: TToggleCandidatePreferredParams,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response: AxiosResponse<TToggleCandidatePreferredResponse> =
        await axios.post("/v1/centres/preferred-candidate", payload);
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data;
    } catch (error) {
      return rejectWithValue((error as any).response?.data || error);
    }
  }
);

export const rateCandidateThunk = createAsyncThunk<
  TRateCandidateResponse,
  TRateCandidateParams[],
  { rejectValue: any }
>("vacancy/rateCandidate", async (params, { rejectWithValue, dispatch }) => {
  try {
    const response: AxiosResponse<TRateCandidateResponse> = await axios.post(
      "/v1/vacancies/candidates/rate",
      { rating_block: params }
    );

    if (response.data?.data?.token) {
      await dispatch(setTokenAction(response.data.data.token));
    }

    return response.data;
  } catch (err) {
    return rejectWithValue((err as any).response?.data || err);
  }
});

export const getCandidatesForRatingThunk = createAsyncThunk(
  "vacancy/getCandidatesForRatingThunk",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TNonRatedCandidatesResponse> =
        await axios.get("/v1/non-rated-candidates");

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

// GET scheduled vacancies
export const getScheduledVacancies = createAsyncThunk(
  "vacancy/getScheduledVacancies",
  async (
    {
      page = 1,
      per_page = DEFAULT_ITEMS_PER_PAGE,
      sort_order = "asc",
      sortBy,
      centre_uuid,
    }: {
      page?: string | number | null | Array<number>;
      per_page?: number;
      sort_order: string;
      sortBy?: string;
      centre_uuid?: string | null;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", String(page || 1));
      queryParams.append("per_page", String(per_page || DEFAULT_ITEMS_PER_PAGE));

      if (sortBy) queryParams.append("sort_by", sortBy);
      if (sort_order) queryParams.append("sort_order", sort_order);
      // Only include centre_uuid if it's valid
      let rawCentreUUIDParam = "";
      if (Array.isArray(centre_uuid)) {
        rawCentreUUIDParam = `&centre_uuid=[${centre_uuid
          .map((uuid) => `'${uuid}'`)
          .join(",")}]`;
      } else if (typeof centre_uuid === "string" && centre_uuid.trim() !== "") {
        rawCentreUUIDParam = `&centre_uuid=['${centre_uuid}']`;
      }
      const finalUrl = `/v1/vacancies/scheduled?${queryParams.toString()}${rawCentreUUIDParam}`;
      const response: AxiosResponse<TGetSchduledVacancyListResponse> =
        await axios.get(finalUrl);
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      if (response.data.data.scheduled_vacancies) {
        await dispatch(setScheduledVacanciesAction(response.data.data));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

// GET urgent vacancies
export const getUrgentVacancies = createAsyncThunk(
  "vacancy/getUrgentVacancies",
  async (
    {
      page = 1,
      per_page = DEFAULT_ITEMS_PER_PAGE,
      sort_order = "asc",
      sort_by,
      centre_uuid,
    }: {
      page?: string | number | null | Array<number>;
      per_page?: number;
      sort_order: string;
      sort_by?: string;
      centre_uuid?: string | null;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", String(page || 1));
      queryParams.append("per_page", String(per_page || DEFAULT_ITEMS_PER_PAGE));

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);
      // Only include centre_uuid if it's valid
      let rawCentreUUIDParam = "";
      if (Array.isArray(centre_uuid)) {
        rawCentreUUIDParam = `&centre_uuid=[${centre_uuid
          .map((uuid) => `'${uuid}'`)
          .join(",")}]`;
      } else if (typeof centre_uuid === "string" && centre_uuid.trim() !== "") {
        rawCentreUUIDParam = `&centre_uuid=['${centre_uuid}']`;
      }
      const finalUrl = `/v1/vacancies/urgent?${queryParams.toString()}${rawCentreUUIDParam}`;
      const response: AxiosResponse<TGetUrgentVacancyListResponse> =
        await axios.get(finalUrl);
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      if (response.data.data.urgent_vacancies) {
        await dispatch(setUrgentVacanciesAction(response.data.data));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * GET method
 * getVacancyHistory - Fetch history of  vacancies with optional sorting
 * @queryParams sort_order=asc/desc, sort_by=candidate|timeframe
 */
export const getVacancyHistory = createAsyncThunk(
  "vacancy/getVacancyHistory",
  async (
    { page, per_page, sort_by, sort_order, centre_uuid }: THistoryVacancyParams,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", String(page));
      queryParams.append("per_page", String(per_page));

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);

      // Only include centre_uuid if it's valid
      let rawCentreUUIDParam = "";
      if (Array.isArray(centre_uuid)) {
        rawCentreUUIDParam = `&centre_uuid=[${centre_uuid
          .map((uuid) => `'${uuid}'`)
          .join(",")}]`;
      } else if (typeof centre_uuid === "string" && centre_uuid.trim() !== "") {
        rawCentreUUIDParam = `&centre_uuid=['${centre_uuid}']`;
      }

      const finalUrl = `/v1/vacancies/history?${queryParams.toString()}${rawCentreUUIDParam}`;

      const response: AxiosResponse<TVacancyHistoryListResponse> =
        await axios.get(finalUrl);

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

/**
 * POST method
 * selectCandidateForVacancyThunk - to select the candidate for the vacancy
 */
export const selectCandidateForVacancyThunk = createAsyncThunk(
  "centers/selectCandidateForVacancyThunk",
  async (
    data: TSelectCandidateForVacancyPayload,
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response: AxiosResponse<TSelectCandidateForVacancyResponse> =
        await axios.post(`/v1/vacancies/candidates/select`, data);
      await dispatch(setTokenAction(response.data.data.token));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

// GET vacancy by UUID
export const getAppliedCandidatesDetailsForVacancy = createAsyncThunk(
  "vacancy/getAppliedCandidatesDetailsForVacancy",
  async (
    {
      uuid,
      page,
      per_page,
    }: {
      uuid: string;
      page?: string | number | null | Array<number>;
      per_page: number;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response: AxiosResponse<TGetAppliedCandidatesDetailsForVacancyResponse> =
        await axios.get(`/v1/vacancies/${uuid}/applications`, {
          params: { page, per_page },
        });

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

/**
 * GET method
 * getVacancyHistory - Fetch history of  vacancies with optional sorting
 * @queryParams sort_order=asc/desc, sort_by=candidate|timeframe
 */
export const getAdminVacancyHistoryThunk = createAsyncThunk(
  "vacancy/getAdminVacancyHistoryThunk",
  async (
    {
      page = 1,
      per_page = DEFAULT_ITEMS_PER_PAGE,
      sort_order = "asc",
      sort_by,
      centre_uuid,
    }: {
      page?: string | number | null | Array<number>;
      per_page?: number;
      sort_order: string;
      sort_by?: string;
      centre_uuid?: string | null;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const queryParams = new URLSearchParams();

      queryParams.append("page", String(page || 1));
      queryParams.append("per_page", String(per_page || DEFAULT_ITEMS_PER_PAGE));

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);
      // Only include centre_uuid if it's valid
      let rawCentreUUIDParam = "";
      if (Array.isArray(centre_uuid)) {
        rawCentreUUIDParam = `&centre_uuid=[${centre_uuid
          .map((uuid) => `'${uuid}'`)
          .join(",")}]`;
      } else if (typeof centre_uuid === "string" && centre_uuid.trim() !== "") {
        rawCentreUUIDParam = `&centre_uuid=['${centre_uuid}']`;
      }
      const finalUrl = `/v1/vacancies/history?${queryParams.toString()}${rawCentreUUIDParam}`;

      const response: AxiosResponse<TGetHistoryVacancyListResponse> =
        await axios.get(finalUrl);

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      if (response.data.data.history_vacancies) {
        await dispatch(setHistoryVacanciesAction(response.data.data));
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

/**
 * POST method
 * cancelVacancyThunk - to cancel a vacancy by UUID
 */
export const cancelVacancyThunk = createAsyncThunk(
  "vacancies/cancelVacancyThunk",
  async (
    {
      active_vacancy_type,
      uuid,
      reason,
    }: {
      active_vacancy_type: "pending" | "accepted";
      uuid: string;
      reason: string | null;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response: AxiosResponse<TCancelVacancyResponse> = await axios.post(
        `/v1/vacancies/${uuid}/cancel`,
        { reason }
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data.data.vacancy;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

// GET vacancy by UUID
export const getCentreDetailsForVacancyThunk = createAsyncThunk(
  "vacancy/getCentreDetailsForVacancyThunk",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetCentreDetailsForVacancyResponse> = await axios.get(
        `/v1/centres/centres-list`
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);